import * as React from 'react';
import type { LucideIcon } from 'lucide-react';

import { cn } from '@/lib/utils';

/* The focus ring is drawn inset rather than as an outer glow: these controls sit
   30px apart in dense toolbars, and an outer ring would overlap its neighbours. */
export const inputClassName =
  'h-[30px] w-full rounded border border-line bg-surface px-2.5 text-sm text-ink transition-colors ' +
  'placeholder:text-ink-subtle focus-visible:border-line-strong focus-visible:outline-none ' +
  'focus-visible:ring-1 focus-visible:ring-inset focus-visible:ring-line-strong ' +
  'disabled:cursor-not-allowed disabled:bg-surface-sunken disabled:text-ink-subtle';

export type InputProps = React.InputHTMLAttributes<HTMLInputElement>;

const Input = React.forwardRef<HTMLInputElement, InputProps>(({ className, type = 'text', ...props }, ref) => (
  <input ref={ref} type={type} className={cn(inputClassName, className)} {...props} />
));
Input.displayName = 'Input';

export interface InputWithIconProps extends InputProps {
  icon: LucideIcon;
  containerClassName?: string;
}

const InputWithIcon = React.forwardRef<HTMLInputElement, InputWithIconProps>(
  ({ icon: Icon, className, containerClassName, ...props }, ref) => (
    <div className={cn('relative w-full', containerClassName)}>
      <Icon
        className="pointer-events-none absolute left-[10px] top-1/2 size-3.5 -translate-y-1/2 text-ink-subtle"
        aria-hidden="true"
      />
      <Input ref={ref} className={cn('pl-[30px]', className)} {...props} />
    </div>
  ),
);
InputWithIcon.displayName = 'InputWithIcon';

export { Input, InputWithIcon };
